import { HttpApiEndpoint, HttpApiGroup } from "@effect/platform";
import * as S from "effect/Schema";
import { PagingParams, pagedResponse } from "./paging.js";
import { PrincipalMiddleware } from "./principal.js";
import { Forbidden } from "./shares.js";
import { LanguageSchema, MeasureAssignmentStatusSchema, MeasureCategorySchema, csvOf, } from "./column-schemas.js";
const Csv = S.transform(S.String, S.Array(S.String), {
    strict: true,
    decode: (value) => value.split(",").filter(Boolean),
    encode: (values) => values.join(","),
});
const OptionalText = S.NullOr(S.String);
export const AuthorizedMeasureAssignment = S.Struct({
    id: S.UUID,
    measure_id: S.UUID,
    user_id: S.UUID,
    client_id: S.UUID,
    status: S.String,
    date_created: OptionalText,
    due_date: OptionalText,
    completed_at: OptionalText,
    title: OptionalText,
    title_t_en: OptionalText,
    description: OptionalText,
    description_t_en: OptionalText,
    type: OptionalText,
    category: OptionalText,
    priority: OptionalText,
    recurring: S.Boolean,
    recurrence: OptionalText,
    valid_until: OptionalText,
    assignee_first_name: OptionalText,
    assignee_last_name: OptionalText,
    assigned_by: S.NullOr(S.UUID),
    assigned_by_first_name: OptionalText,
    assigned_by_last_name: OptionalText,
    online_course_id_de: S.NullOr(S.UUID),
    online_course_id_en: S.NullOr(S.UUID),
    online_course_title_de: OptionalText,
    online_course_title_en: OptionalText,
    online_course_description_de: OptionalText,
    online_course_description_en: OptionalText,
    online_course_url_de: OptionalText,
    online_course_url_en: OptionalText,
    document_id_de: S.NullOr(S.UUID),
    document_id_en: S.NullOr(S.UUID),
    document_filename_download_de: OptionalText,
    document_filename_download_en: OptionalText,
    attachment_id: S.NullOr(S.UUID),
    attachment_filename_download: OptionalText,
    metadata: S.Unknown,
});
export const ListAuthorizedMeasureAssignmentsParams = S.extend(PagingParams, S.Struct({
    client_id: S.optional(S.UUID),
    user_id: S.optional(S.UUID),
    // Membership filter — distinct from `user_id`'s single-employee scope.
    // Bounded by the shared page cap (`PagingParams.limit`, max 100): a
    // caller checking a larger audience must chunk into multiple requests.
    user_ids: S.optional(Csv),
    measure_id: S.optional(S.UUID),
    category: S.optional(MeasureCategorySchema),
    statuses: S.optional(csvOf(MeasureAssignmentStatusSchema)),
    excluded_statuses: S.optional(csvOf(MeasureAssignmentStatusSchema)),
    search: S.optional(S.String),
    asset_type: S.optional(S.Literal("document", "online_course")),
    asset_translation_group_id: S.optional(S.NumberFromString.pipe(S.int())),
    language: S.optional(LanguageSchema),
    // Additive, opt-in (PMO-245): narrows a recurring measure's rows to one
    // per person via `vw_measure_assignments.is_current`. Omitted by default
    // so per-cycle consumers keep every row.
    is_current: S.optional(S.BooleanFromString),
}));
export const AuthorizedActivity = S.Struct({
    id: S.Int,
    user_id: S.NullOr(S.UUID),
    timestamp: S.String,
    activity_type: OptionalText,
    item_id: S.NullOr(S.UUID),
    client_id: S.NullOr(S.UUID),
    status: OptionalText,
    assigned_by: S.NullOr(S.UUID),
    action: OptionalText,
    measure_id: S.NullOr(S.UUID),
    total_assignees: S.NullOr(S.Number),
    assigned_by_name: OptionalText,
    user_name: OptionalText,
    file_name: OptionalText,
    online_course_title: OptionalText,
    measure_title: OptionalText,
});
export const ListAuthorizedActivityParams = S.extend(PagingParams, S.Struct({
    client_id: S.UUID,
    user_id: S.optional(S.UUID),
    measure_id: S.optional(S.UUID),
    exclude_activity_type: S.optional(S.String),
}));
export const InspectionContributor = S.Struct({
    id: S.UUID,
    first_name: OptionalText,
    last_name: OptionalText,
    email: OptionalText,
    role: S.NullOr(S.UUID),
    client_id: S.NullOr(S.UUID),
    /**
     * `yp_users.status`. Callers that turn this list into an assignment audience
     * need it: a suspended or archived person is silently skipped when the share
     * is applied, so offering them in a picker promises work that never arrives.
     */
    status: OptionalText,
    metadata: S.Unknown,
});
export const InspectionLocationManager = S.extend(InspectionContributor, S.Struct({ location_ids: S.Array(S.UUID) }));
export const ListInspectionLocationManagersParams = S.Struct({
    client_id: S.UUID,
    location_id: S.UUID,
});
/**
 * Locations per request when resolving their managers. The offline sync
 * engine holds one location per active inspection, so a cold pass asked for
 * ~50 of them one at a time; ids ride a POST body rather than a query string
 * so the set cannot outgrow a URL length limit.
 */
export const INSPECTION_LOCATION_MANAGER_BATCH_SIZE = 200;
export const ListInspectionLocationManagersByLocationsPayload = S.Struct({
    client_id: S.UUID,
    location_ids: S.Array(S.UUID).pipe(S.maxItems(INSPECTION_LOCATION_MANAGER_BATCH_SIZE)),
});
export const ListInspectionContributorsParams = S.extend(PagingParams, S.Struct({
    client_id: S.UUID,
    internal_role_ids: S.optional(Csv),
    search: S.optional(S.String),
}));
export const VisibleInspectionQuestionnaire = S.Struct({
    id: S.UUID,
    title: S.String,
    description: OptionalText,
    type: S.String,
    reason: OptionalText,
    translation_group_id: S.Int,
    assign_all: S.Boolean,
    client_ids: S.Array(S.UUID),
    created_by: S.NullOr(S.UUID),
    date_created: OptionalText,
    date_updated: OptionalText,
    language: LanguageSchema,
});
export const ListVisibleInspectionQuestionnairesParams = S.extend(PagingParams, S.Struct({
    client_id: S.UUID,
    language: LanguageSchema,
    search: S.optional(S.String),
}));
export const ListAuthorizedActivity = HttpApiEndpoint.get("listActivity", "/authorized/activity")
    .setUrlParams(ListAuthorizedActivityParams)
    .addSuccess(pagedResponse(AuthorizedActivity))
    .addError(Forbidden, { status: 403 });
export const authorizedReadsGroup = HttpApiGroup.make("authorized-reads")
    .add(HttpApiEndpoint.get("listMeasureAssignments", "/authorized/measure-assignments")
    .setUrlParams(ListAuthorizedMeasureAssignmentsParams)
    .addSuccess(pagedResponse(AuthorizedMeasureAssignment))
    .addError(Forbidden, { status: 403 }))
    .add(ListAuthorizedActivity)
    .add(HttpApiEndpoint.get("listInspectionContributors", "/authorized/inspection-contributors")
    .setUrlParams(ListInspectionContributorsParams)
    .addSuccess(pagedResponse(InspectionContributor))
    .addError(Forbidden, { status: 403 }))
    .add(HttpApiEndpoint.get("listInspectionLocationManagers", "/authorized/inspection-location-managers")
    .setUrlParams(ListInspectionLocationManagersParams)
    .addSuccess(S.Struct({ items: S.Array(InspectionLocationManager) }))
    .addError(Forbidden, { status: 403 }))
    .add(HttpApiEndpoint.post("listInspectionLocationManagersByLocations", "/authorized/inspection-location-managers/by-locations")
    .setPayload(ListInspectionLocationManagersByLocationsPayload)
    .addSuccess(S.Struct({ items: S.Array(InspectionLocationManager) }))
    .addError(Forbidden, { status: 403 }))
    .add(HttpApiEndpoint.get("listVisibleInspectionQuestionnaires", "/authorized/inspection-questionnaires")
    .setUrlParams(ListVisibleInspectionQuestionnairesParams)
    .addSuccess(pagedResponse(VisibleInspectionQuestionnaire))
    .addError(Forbidden, { status: 403 }))
    .middleware(PrincipalMiddleware);
//# sourceMappingURL=authorized-reads.js.map